/* eslint-disable no-unused-vars */
import {useCallback, useMemo, useState, useEffect} from "react";
import {useHistory} from "react-router-dom";
import {DashboardLayout} from "components/Layout";
import {BasicTable, SearchBox} from "components/Table";
import {isMobile} from "react-device-detect";
import {
	useDoctorPrices,
	useBulkDeleteDoctorPrice,
	useSendOtpDoctorPrice,
	useValidateOtpDoctorPrice,
} from "hooks/AdminHospital/DoctorPrice";
import DOCTOR_PRICE_STATUS from "config/doctorPriceStatus";
import {useToast} from "hooks/useToast";
import {convertErrorMessageFormat} from "utils/converter";
import {ModalConfirmation, ModalContainer} from "components/Modal";
import {OtpInput, Select} from "components/Form";
import {DropdownAsync} from "components/Dropdown/DropdownAsync";
import {getDoctors} from "hooks/Doctor";
import {getHospitals} from "hooks/SuperAdmin/Hospital";

export function PriceListPage() {
	const [dataLimit, setDataLimit] = useState(10);
	const [searchKeyword, setSearchKeyword] = useState("");
	const {data: prices, isLoading, filterPrices, setFilterPrices} = useDoctorPrices();

	const statusStyle = useCallback(status => {
		switch (status) {
			case DOCTOR_PRICE_STATUS.WAITING:
				return {
					backgroundColor: "#FFF7E1",
					color: "#FB9C18",
					cursor: "pointer",
					textDecoration: "underline",
				};
			case DOCTOR_PRICE_STATUS.APPROVED:
				return {
					backgroundColor: "#DFFFDA",
					color: "#39B50E",
				};
			default:
				return {
					backgroundColor: "#FFD8D8",
					color: "#F21A1A",
				};
		}
	}, []);

	const {showToast} = useToast();
	const {mutate, isLoading: isDeletingData} = useBulkDeleteDoctorPrice();
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [deleteItemData, setDeleteItemData] = useState({id: null, name: null});
	const handleDeleteData = () => {
		mutate(
			{doctorUserId: deleteItemData.doctorUserId, hospitalId: deleteItemData?.hospitalId},
			{
				onSuccess: () => {
					showToast("success", `Data harga ${deleteItemData?.doctor?.fullName} berhasil dihapus`, 3000);
					window.scrollTo(0, 0);
					setShowDeleteModal(false);
				},
				onError: res => {
					showToast("error", convertErrorMessageFormat(res.response.status, res.response.data.message), null);
				},
			},
		);
	};

	const [isOpenOtp, setIsOpenOtp] = useState(false);
	const [otpValue, setOtpValue] = useState("");
	const {mutate: sendOtp, isLoading: isSendingOtp} = useSendOtpDoctorPrice();
	const handleSendOtp = () => {
		sendOtp(
			{channel: "whatsapp"},
			{
				onSuccess: () => {
					showToast("success", "OTP berhasil dikirim ke whatsapp Anda", 3000);
					setShowDeleteModal(false);
					setIsOpenOtp(true);
				},
				onError: res => {
					showToast("error", convertErrorMessageFormat(res.response.status, res.response.data.message), null);
				},
			},
		);
	};
	const {mutate: validateOtp, isLoading: isValidatingOtp} = useValidateOtpDoctorPrice();
	const handleSubmitOtp = () => {
		if (otpValue.length === 6) {
			validateOtp(
				{otp: otpValue},
				{
					onSuccess: () => {
						showToast("success", "OTP berhasil divalidasi", 3000);
						setIsOpenOtp(false);
						handleDeleteData();
					},
					onError: res => {
						showToast(
							"error",
							convertErrorMessageFormat(res.response.status, res.response.data.message),
							null,
						);
					},
				},
			);
		}
	};

	const fetchDoctorsData = async (search, callback) => {
		const response = await getDoctors(null, {page: 1, search, limit: 10});
		const {rows} = response?.data ?? {};

		callback(rows.map(item => ({label: `${item.firstName} ${item.lastName}`, value: item.id})));
	};
	const [defaultDoctorOpts, setDefaultDoctorOpts] = useState([]);

	useEffect(() => {
		const getDefaultDoctorOpts = async () => {
			const response = await getDoctors(null, {page: 1, limit: 10});
			const {rows} = response?.data ?? {};

			setDefaultDoctorOpts(rows.map(item => ({label: `${item.firstName} ${item.lastName}`, value: item.id})));
		};

		if (defaultDoctorOpts.length === 0) {
			getDefaultDoctorOpts();
		}
	}, []);

	const fetchHospitalsData = async (search, callback) => {
		const response = await getHospitals(null, {page: 1, search, limit: 10});
		const {rows} = response?.data ?? {};

		callback(rows.map(item => ({label: `${item.name}`, value: item.id})));
	};
	const [defaultHospitalOpts, setDefaultHospitalOpts] = useState([]);

	useEffect(() => {
		const getDefaultHospitalOpts = async () => {
			const response = await getHospitals(null, {page: 1, limit: 10});
			const {rows} = response?.data ?? {};

			setDefaultHospitalOpts(rows.map(item => ({label: `${item.name}`, value: item.id})));
		};

		if (defaultHospitalOpts.length === 0) {
			getDefaultHospitalOpts();
		}
	}, []);

	const history = useHistory();
	const columns = useMemo(
		() => [
			{
				Header: "No",
				accessor: "idSerial",
			},
			{
				Header: "Nama Dokter",
				accessor: "doctor.fullName",
			},
			{
				Header: "Harga",
				accessor: "priceRange",
			},
			{
				Header: "Rumah Sakit",
				accessor: "hospital.name",
			},
			{
				Header: "Status",
				accessor: "status",
				Cell: ({row}) => (
					<span class={`px-4 py-2 rounded-3`} style={statusStyle(row?.original?.status)}>
						{row?.original?.status}
					</span>
				),
			},
			{
				Header: "Aksi",
				accessor: "id",
				Cell: ({row}) => (
					<>
						<a
							class="text-orange wait-pay"
							style={{cursor: "pointer"}}
							onClick={() => history.push(`/doctor/price/list/${row?.original?.doctor?.id}`)}>
							<span class="icon-ico-eye-open ft-18" />
						</a>
						<a
							class="text-secondary wait-pay ms-2"
							style={{cursor: "pointer"}}
							onClick={() => {
								setDeleteItemData(row?.original);
								setShowDeleteModal(true);
							}}>
							<span class="icon-ico-delete ft-18" />
						</a>
					</>
				),
			},
		],
		[],
	);

	const columnsMobile = useMemo(
		() => [
			{
				Header: "No",
				accessor: "idSerial",
			},
			{
				Header: "Nama Dokter",
				accessor: "doctor.fullName",
			},
			{
				Header: "Harga",
				accessor: "priceRange",
			},
			{
				Header: "Status",
				accessor: "status",
				Cell: ({row}) => (
					<span class={`px-4 py-2 rounded-3`} style={statusStyle(row?.original?.status)}>
						{row?.original?.status}
					</span>
				),
			},
			{
				Header: "Aksi",
				accessor: "id",
				Cell: ({row}) => (
					<>
						<a
							class="text-orange wait-pay"
							style={{cursor: "pointer"}}
							onClick={() => history.push(`/doctor/price/list/${row?.original?.doctor?.id}`)}>
							<span class="icon-ico-eye-open ft-18" />
						</a>
						<a
							class="text-secondary wait-pay ms-2"
							style={{cursor: "pointer"}}
							onClick={() => {
								setDeleteItemData(row?.original);
								setShowDeleteModal(true);
							}}>
							<span class="icon-ico-delete ft-18" />
						</a>
					</>
				),
			},
		],
		[],
	);

	const dataWithIDSerial =
		prices?.data?.rows?.map((item, index) => ({
			...item,
			idSerial: (prices?.data?.currentPage - 1) * dataLimit + index + 1,
		})) || [];

	return (
		<DashboardLayout>
			<>
				<ModalConfirmation
					approveLabel="Ya, Kirim OTP"
					description={`Apakah Anda yakin ingin menghapus data harga "${deleteItemData?.doctor?.fullName}"?`}
					loading={isDeletingData}
					rejectLabel="Batal"
					visible={showDeleteModal}
					onApprove={handleSendOtp}
					onClose={() => setShowDeleteModal(false)}
					onReject={() => setShowDeleteModal(false)}
				/>
				<ModalContainer title="Masukkan Kode OTP" visible={isOpenOtp} onClose={() => setIsOpenOtp(false)}>
					<div className="box-popup w-25" id="change-price">
						<div className="for-close">
							<button className="btn btn-close" type="button" onClick={() => setIsOpenOtp(false)} />
						</div>
						<div class="mb-3">
							<p class="text-center fw-bold fs-4" for="otp">
								Kode OTP
							</p>
							<p class="text-center text-secondary">
								Silakan masukkan kode OTP yang telah kami kirim ke nomor telepon Anda
							</p>
							<OtpInput
								className="col-2"
								inputStyle="col-3 form-control otp-form"
								numInputs={6}
								value={otpValue}
								onChange={setOtpValue}
							/>
						</div>
						<div class="d-flex flex-column justify-content-end mt-5">
							<button class="btn btn-orange" disabled={isValidatingOtp} onClick={handleSubmitOtp}>
								{isValidatingOtp ? "Mengirim..." : "Kirim"}
							</button>
							<button
								class="btn btn-text text-green mt-3"
								disabled={isSendingOtp}
								onClick={handleSendOtp}>
								{isSendingOtp ? "Mengirim..." : "Kirim Ulang Kode"}
							</button>
						</div>
					</div>
				</ModalContainer>
				<div class="text-bold text-dark mb-4">Harga</div>
				<SearchBox
					rightElement={
						<div className="d-flex align-items-center">
							<span className="me-2 fw-bold">Filter:</span>
							<div className="me-2" style={{width: "200px"}}>
								<DropdownAsync
									defaultOptions={defaultDoctorOpts}
									fetchOptions={fetchDoctorsData}
									label="Pilih Dokter"
									onChange={val => {
										setFilterPrices({
											...filterPrices,
											doctorUserId: val?.value || null,
											page: 1,
											limit: dataLimit,
										});
									}}
								/>
							</div>
							<div className="me-2" style={{width: "200px"}}>
								<DropdownAsync
									defaultOptions={defaultHospitalOpts}
									fetchOptions={fetchHospitalsData}
									label="Pilih Rumah Sakit"
									onChange={val => {
										setFilterPrices({
											...filterPrices,
											hospitalId: val?.value || null,
											page: 1,
											limit: dataLimit,
										});
									}}
								/>
							</div>
							<div style={{width: "200px", paddingTop: "16px"}}>
								<Select
									options={Object.values(DOCTOR_PRICE_STATUS).map(item => ({
										label: item,
										value: item,
									}))}
									placeholder="Status"
									onChange={val => {
										setFilterPrices({
											...filterPrices,
											status: val?.value || null,
											page: 1,
											limit: dataLimit,
										});
									}}
								/>
							</div>
						</div>
					}
					onSearch={keyword => {
						setFilterPrices({...filterPrices, search: keyword, page: 1, limit: dataLimit});
						setSearchKeyword(keyword);
					}}
				/>

				<BasicTable
					columns={isMobile ? columnsMobile : columns}
					currentPage={prices?.data?.currentPage}
					data={dataWithIDSerial}
					limit={dataLimit}
					loading={isLoading}
					striped={true}
					title="Data Dokter"
					totalItems={prices?.data?.totalItems}
					totalPage={prices?.data?.totalPages}
					onLimitChange={limit => {
						setFilterPrices({...filterPrices, limit, page: 1, search: searchKeyword});
						setDataLimit(limit);
					}}
					onPageChange={selectedPage =>
						setFilterPrices({...filterPrices, page: selectedPage, limit: dataLimit, search: searchKeyword})
					}
				/>
			</>
		</DashboardLayout>
	);
}
